import { z } from 'zod';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';
import { getHeatmapOverviewExplanationOutputKey } from 'libs/end-to-end/src/explanations-llm-types';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { getLevelByColumnValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';

/**
 * Defines a metric option with its focus area and specific measurement
 */
export const HeatmapMetricOptionsDefinition = z
  .object({
    focusArea: FocusArea,
    metric: z.string(),
    label: z.string(),
  })
  .describe('HeatmapMetricOptionsDefinition');
export type HeatmapMetricOptionsDefinition = z.infer<
  typeof HeatmapMetricOptionsDefinition
>;

/**
 * Defines a metric with its focus area and specific measurement
 * The options property is only used if there is a focus area with more than 1 metric
 * in a recursive manner
 */
export const HeatmapMetricDefinition = z
  .object({
    focusArea: FocusArea,
    metric: z.string(),
    label: z.string().optional(),
    options: z.array(HeatmapMetricOptionsDefinition).optional(),
    visible: z.boolean().optional(),
  })
  .describe('HeatmapMetricDefinition');
export type HeatmapMetricDefinition = z.infer<typeof HeatmapMetricDefinition>;

export const HeatmapImpactScores = z
  .record(z.array(z.number().nullable()))
  .describe('HeatmapImpactScores');
export type HeatmapImpactScores = z.infer<typeof HeatmapImpactScores>;

export const HeatmapValues = z
  .record(z.array(z.number().nullable()))
  .describe('HeatmapValues');

export type HeatmapValues = z.infer<typeof HeatmapValues>;

export const HeatmapConfidenceLevels = z
  .record(z.array(z.number().nullable()))
  .describe('HeatmapConfidenceLevels');
export type HeatmapConfidenceLevels = z.infer<typeof HeatmapConfidenceLevels>;

export const HeatmapSampleSizes = z
  .record(z.array(z.number().nullable()))
  .describe('HeatmapSampleSizes');
export type HeatmapSampleSizes = z.infer<typeof HeatmapSampleSizes>;

export const HeatmapDegreesOfFreedom = z
  .record(z.array(z.number().nullable()))
  .describe('HeatmapDegreesOfFreedom');
export type HeatmapDegreesOfFreedom = z.infer<typeof HeatmapDegreesOfFreedom>;

/**
 * Contains data for a specific filter including:
 * - groups: Categories being compared
 * - metrics: List of metrics being measured
 * - impactScores: Impact scores for each group/metric combination
 * - values: Raw values for each group/metric combination
 * - confidenceLevels: Confidence levels for each group/metric combination
 * - sampleSizes: Sample sizes for each group/metric combination
 */
export const HeatmapFilterData = z
  .object({
    groups: z.array(z.string()),
    metrics: z.array(HeatmapMetricDefinition),
    impactScores: HeatmapImpactScores,
    values: HeatmapValues,
    confidenceLevels: HeatmapConfidenceLevels,
    sampleSizes: HeatmapSampleSizes,
    degreesOfFreedom: HeatmapDegreesOfFreedom,
  })
  .describe('HeatmapFilterData');
export type HeatmapFilterData = z.infer<typeof HeatmapFilterData>;

/**
 * Groups filter data under a specific filter name
 */
export const HeatmapFilterGrouping = z
  .object({
    filterName: z.string(),
    filterData: HeatmapFilterData,
    visible: z.boolean().optional(),
  })
  .describe('HeatmapFilterGrouping');
export type HeatmapFilterGrouping = z.infer<typeof HeatmapFilterGrouping>;

/**
 * Represents disparity analysis results for a specific dimension and treatment level,
 * containing multiple filter groupings
 */
export const HeatmapFilterGroupings = z
  .array(HeatmapFilterGrouping)
  .describe('HeatmapFilterGroupings');

export const HeatmapDisparityResultDetails = z
  .object({
    dimension: z.string(),
    treatmentLevel: z.string(),
    filterGroupings: HeatmapFilterGroupings,
  })
  .describe('HeatmapDisparityResultDetails');

export type HeatmapDisparityResultDetails = z.infer<
  typeof HeatmapDisparityResultDetails
>;

export const HeatmapDisparityResult = z
  .object({
    disparityResult: HeatmapDisparityResultDetails,
  })
  .describe('HeatmapDisparityResult');

export type HeatmapDisparityResult = z.infer<typeof HeatmapDisparityResult>;

/**
 * Top-level schema for heatmap analysis output.
 * Contains an array of disparity results that show differences
 * across various dimensions and treatment levels.
 */
export const HeatmapAnalysisOutputDisparityResults = z
  .array(HeatmapDisparityResult)
  .describe('HeatmapAnalysisOutputDisparityResults');

export type HeatmapAnalysisOutputDisparityResults = z.infer<
  typeof HeatmapAnalysisOutputDisparityResults
>;

export const HeatmapAnalysisControlledDisparitiesOutput = z
  .object({
    disparityResults: HeatmapAnalysisOutputDisparityResults,
  })
  .describe('HeatmapAnalysisControlledDisparitiesOutput');

export const HeatmapAnalysisMetricAveragesOutput = z
  .object({
    result: z.boolean(),
  })
  .describe('HeatmapAnalysisMetricAveragesOutput');

export const HeatmapAnalysisOutput = z
  .object({
    controlledDisparities:
      HeatmapAnalysisControlledDisparitiesOutput.nullable(),
    metricAverages: HeatmapAnalysisMetricAveragesOutput.nullable(),
    explanations: ExplanationsOutputDictionary.optional(),
  })
  .describe('HeatmapAnalysisOutput');

export type HeatmapAnalysisOutput = z.infer<typeof HeatmapAnalysisOutput>;

export const HeatmapAnalysisOutputFile = 'heatmap_analysis_output.json';
export const HeatmapAnalysisOutputFinalFile =
  'heatmap_analysis_output_final.json';

// Define the schema for the heatmap table cell
export const HeatmapTableCell = z.object({
  // Row
  group: z.string(),
  // Column
  focusArea: FocusArea,
  metric: z.string(),
  metricLabel: z.string(),
  // Cell
  impactScore: z.number().nullable(),
  confidenceLevel: z.number().nullable(),
  sampleSize: z.number().nullable(),
  disparityValue: z.number().nullable(),
});
export type HeatmapTableCell = z.infer<typeof HeatmapTableCell>;

export const HeatmapTableSerializableProps = z.object({
  backgroundColorClass: z.union([
    z.literal('bg-white'),
    z.literal('bg-transparent'),
  ]),
  hideSearch: z.boolean(),
  shouldScroll: z.boolean(),
  /** The cells to display in the head row */
  header: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      className: z.string().optional(),
    }),
  ),
  /** The rows to display in the body */
  body: z.array(
    z.object({
      key: z.string(),
      cells: z.array(
        z.object({
          key: z.string(),
          content: z.string(),
          className: z.string().optional(),
          style: z
            .object({
              backgroundColor: z.string().optional(),
              color: z.string().optional(),
            })
            .optional(),
        }),
      ),
    }),
  ),
  filterName: z.string().optional(),
});

export type HeatmapTableSerializableProps = z.infer<
  typeof HeatmapTableSerializableProps
>;

/**
 * Retrieves the disparity results by dimension and treatment level.
 *
 * @param {HeatmapDisparityResult[]} disparityResults - The array of disparity results
 * @param {string | undefined} dimension - The dimension to filter by
 * @param {string | undefined} treatmentLevel - The treatment level to filter by
 * @returns {HeatmapDisparityResult | null} The disparity result or null if not found
 */
export const getDisparityResultsByDimensionAndTreatmentLevel = ({
  disparityResults,
  dimension,
  treatmentLevel,
}: {
  disparityResults: HeatmapDisparityResult[];
  dimension: string | undefined;
  treatmentLevel: string | undefined;
}): HeatmapDisparityResult | null => {
  // First try to find exact match
  const exactMatch = disparityResults.find(
    (result) =>
      result.disparityResult.dimension === dimension &&
      result.disparityResult.treatmentLevel === treatmentLevel,
  );

  // If no match found and array has elements, return first element
  return (
    exactMatch || (disparityResults.length > 0 ? disparityResults[0] : null)
  );
};

export const getFilterDefinitionsForHeatmapAnalysis = ({
  selectedDisparityResults,
  myObservation,
}: {
  selectedDisparityResults: HeatmapDisparityResult | null;
  myObservation: MyObservation | null;
}) => {
  if (!selectedDisparityResults || !myObservation) return [];

  const filterSetDefinitions =
    myObservation.filterSetDefinitionsWithValues.find(
      (f) => f.filterSetName === 'default',
    )?.filterDefinitions;

  return selectedDisparityResults.disparityResult.filterGroupings.map(
    (group) => ({
      value: group.filterName,
      label:
        filterSetDefinitions?.find(
          (f) => f.columnHeaderValue === group.filterName,
        )?.displayName || group.filterName,
    }),
  );
};

// Temporary logic to exclude the year filter from the heatmap.
// This will be removed once we have a better way to handle this.
const groupingToFilter = 'year';

export const filterDisparityResultsByGrouping = (
  disparityResults: HeatmapDisparityResult[],
): HeatmapDisparityResult[] => {
  return disparityResults.map((result) => ({
    ...result,
    disparityResult: {
      ...result.disparityResult,
      filterGroupings: result.disparityResult.filterGroupings.filter(
        (grouping) =>
          typeof grouping.filterName === 'string' &&
          grouping.filterName !== groupingToFilter,
      ),
    },
  }));
};

/**
 * Processes heatmap data to
 * (1) group metrics by focus areas and
 * (2) filter the filterGroupings with the selected filterName.
 *
 * For (1), let´s say we have the following input data:
 *      ...
 *      filterData: {
 *          groups: ["1", "2", "3"],
 *          metrics: [
 *            {
 *              focusArea: "Gender",
 *              metric: "female",
 *              label: "Female"
 *            },
 *            {
 *              focusArea: "Gender",
 *              metric: "male",
 *              label: "Male"
 *            },
 *            {
 *              focusArea: "Age",
 *              metric: "avg_age",
 *              label: "Average Age"
 *            }
 *          ],
 *          impactScores: { ... },
 *          values: {  ... },
 *          confidenceLevels: { ... },
 *          sampleSizes: { ... }
 *      }
 *      ...
 * the grouped metrics will be:
 *      ...
 *      filterData: {
 *          groups: ["1", "2", "3"],
 *          metrics: [
 *            {
 *              focusArea: "Gender",
 *              metric: "female",
 *              label: "Female",
 *              options: [
 *                {
 *                  focusArea: "Gender",
 *                  metric: "male",
 *                  label: "Male"
 *                }
 *              ],
 *              visible: true,
 *            },
 *            {
 *              focusArea: "Age",
 *              metric: "avg_age",
 *              label: "Average Age",
 *              // No options array since it's the only metric in its focus area
 *              visible: true, // Always visible if it's the only metric in its focus area
 *            },
 *            {
 *              focusArea: "Gender",
 *              metric: "male",
 *              label: "Male",
 *              options: [
 *                {
 *                  focusArea: "Gender",
 *                  metric: "female",
 *                  label: "Female"
 *                },
 *              ],
 *              visible: false, // Not visible since it's not the first metric in its focus area
 *            },
 *          ]
 *        },
 *      impactScores: { ... },
 *      values: { ... },
 *      confidenceLevels: { ... },
 *      sampleSizes: { ... }
 *    }
 *
 * @param {Object} params - The parameters object
 * @param {HeatmapDisparityResult[]} [params.selectedDisparityResults] - The input heatmap data to process
 * @param {string} [params.selectedFilterName] - The selected filter name
 * @param {Array<{ displayName: string; focusArea: string }>} [params.metricLabels] - The metric labels
 * @returns {HeatmapDisparityResult} The heatmap data to view,
 *          or null if no input data is provided
 */
export const getFilterGroupingByFilterName = ({
  selectedDisparityResults,
  selectedFilterName,
  metricLabels,
  maxGroupsPerResult,
}: {
  selectedDisparityResults: HeatmapDisparityResult | null;
  selectedFilterName?: string;
  metricLabels: Array<{
    displayName: string;
    focusArea: string;
    metric: string;
  }>;
  /**
   * By default, this is unlimited and will returns all rows.
   * By passing a positive number, you will be able to get multiple results if there are more
   * rows than the specified number. We use this in report generation to prevent the heatmap
   * from being too long, and this will lead to multiple heatmap tables in the report.
   */
  maxGroupsPerResult?: number;
}): HeatmapFilterGrouping[] => {
  if (!selectedDisparityResults) return [];

  const filtered = selectedDisparityResults.disparityResult.filterGroupings
    .filter(
      (filterGrouping) =>
        filterGrouping.filterName === selectedFilterName ||
        (!selectedFilterName &&
          filterGrouping ===
            selectedDisparityResults.disparityResult.filterGroupings[0]),
    )
    .flatMap((filterGrouping) => {
      const { metrics } = filterGrouping.filterData;

      // Update metrics with options where needed
      const updatedMetrics = metrics.map((metric) => {
        const metricLabel = metricLabels.find(
          (label) =>
            label.focusArea === metric.focusArea &&
            label.metric === metric.metric.toLowerCase(),
        );
        // Update the metric label with the displayName if it exists
        metric.label =
          metricLabel?.displayName || metric.label || metric.metric;

        return metric;
      });

      const itemWithAllGroups = {
        ...filterGrouping,
        filterData: {
          ...filterGrouping.filterData,
          metrics: updatedMetrics,
        },
      };

      if (!maxGroupsPerResult || maxGroupsPerResult < 0) {
        return [itemWithAllGroups];
      }

      const results: HeatmapFilterGrouping[] = [];

      const totalGroupCount = filterGrouping.filterData.groups.length;
      const numberOfResults = Math.ceil(totalGroupCount / maxGroupsPerResult);

      // Split the groups into chunks based on maxGroupsPerResult
      for (let i = 0; i < numberOfResults; i++) {
        const startIndex = i * maxGroupsPerResult;
        const endIndex = startIndex + maxGroupsPerResult;
        const groupSubset = filterGrouping.filterData.groups.slice(
          startIndex,
          endIndex,
        );

        if (groupSubset.length === 0) {
          continue;
        }

        results.push({
          ...itemWithAllGroups,
          filterData: {
            ...itemWithAllGroups.filterData,
            groups: groupSubset,
          },
        });
      }

      return results;
    });

  return filtered;
};

export const getMetricLabelsForHeatmapAnalysis = (
  myObservation: MyObservation | null,
) => {
  /**
   * Returns an array of objects like [{ displayName: "Metric1", focusArea: "Focus Area 1" }, ...]
   * The resulting array will contain all metrics across all focus areas, with each metric maintaining
   * its relationship to its parent focus area.
   */
  const metricLabels = myObservation?.focusAreas.flatMap((focusArea) =>
    focusArea.metrics.map((metric) => ({
      metric: metric.columnHeaderValue,
      displayName: metric.displayName,
      focusArea: focusArea.focusArea,
    })),
  );

  return metricLabels ?? [];
};

export const mapHeaderCells = ({
  sortedFocusAreas,
  filteredMetrics,
}: {
  sortedFocusAreas: FocusArea[];
  filteredMetrics: HeatmapMetricDefinition[];
}) => {
  return sortedFocusAreas.flatMap((focusArea) => {
    const metrics = filteredMetrics.filter(
      (metric) => metric.focusArea === focusArea,
    );

    return metrics.map((metric) => ({
      key: `${metric.focusArea}-${metric.metric}`,
      label: metric.label || metric.focusArea,
      focusArea: metric.focusArea,
      metric: metric.metric,
    }));
  });
};

export const formatNumberFn = (value: number | null) =>
  value !== null
    ? `${parseFloat(
        formatNumberForDisplayInUnits({
          value,
          units: 'percentage',
          isPercentageAlreadyMultipliedBy100: false,
        }),
      ).toFixed(0)}%`
    : null;

/**
 * Directly computes compatible data structure from filteredGrouping without
 * the overhead of creating full bodyRows structure. This is more efficient
 * when only the compatibleData format is needed.
 */
export const getCompatibleDataFromFilteredGrouping = ({
  filteredGrouping,
  sortedFocusAreas,
  visibleMetrics,
  headCells,
}: {
  filteredGrouping: HeatmapFilterGrouping;
  sortedFocusAreas: FocusArea[];
  visibleMetrics: string[];
  headCells: Array<{ metric: string }>;
}) => {
  const groups = filteredGrouping.filterData.groups ?? [];
  const groupingMetrics =
    filteredGrouping.filterData.metrics.map((metric, index) => ({
      ...metric,
      index,
    })) ?? [];

  // Filter and order metrics to match headCells structure
  const metrics = sortedFocusAreas.flatMap((focusArea) => {
    return groupingMetrics
      .filter((metric) => metric.focusArea === focusArea)
      .filter((metric) =>
        visibleMetrics.length > 0
          ? visibleMetrics.includes(metric.metric)
          : true,
      );
  });

  const values = filteredGrouping.filterData.values ?? {};
  const impactScores = filteredGrouping.filterData.impactScores ?? {};
  const confidenceLevels = filteredGrouping.filterData.confidenceLevels ?? {};
  const sampleSizes = filteredGrouping.filterData.sampleSizes ?? {};

  // Extract column headers from headCells
  const columnHeaders = headCells.map((cell) => cell.metric);

  // Build rows data directly from filteredGrouping
  // Each row starts with the group name, followed by metric values
  const rows = groups.map((group) => {
    return [
      group, // First element is the group name (used as key in HeatmapViewMode)
      ...metrics.map((metric) => {
        const value = values[group]?.[metric.index];
        return String(value ?? '');
      }),
    ];
  });

  // Create rowsMeta mapping row index to group name
  const rowsMeta = groups.map((group) => ({
    groupByLevel: group,
    secondaryBreakdownLevel: undefined,
  }));

  // Extract sample sizes, confidence values, and impact values directly
  // Each array has the same length as rows (group + metrics)
  // The first element is a placeholder (never accessed since columnIndex > 0 in HeatmapViewMode)
  const sampleSizesArray = groups.map((group) => [
    0, // Placeholder for group column (index 0, never accessed)
    ...metrics.map((metric) => sampleSizes[group]?.[metric.index] ?? 0),
  ]);

  const confidenceValues = groups.map((group) => [
    '0', // Placeholder for group column (index 0, never accessed)
    ...metrics.map((metric) =>
      String(confidenceLevels[group]?.[metric.index] ?? '0'),
    ),
  ]);

  const impactValues = groups.map((group) => [
    '0', // Placeholder for group column (index 0, never accessed)
    ...metrics.map((metric) =>
      String(impactScores[group]?.[metric.index] ?? '0'),
    ),
  ]);

  return {
    columnHeaders,
    rows,
    rowsMeta,
    sampleSizes: sampleSizesArray,
    booleanColumns: [], // Heatmap doesn't have boolean columns
    confidenceValues,
    impactValues,
    fullSampleAverage: [], // Heatmap doesn't have fullSampleAverage
    numeratorSizes: [],
  };
};

/** Function to determine the color of a cell based on its impact score */
export const getColorForValue = (impactScore: number) => {
  const absImpactScore = Math.abs(impactScore);
  if (absImpactScore >= 0.9)
    return '[&_button]:bg-violet-800 [&_button]:text-white';
  if (absImpactScore >= 0.7)
    return '[&_button]:bg-violet-700 [&_button]:text-white';
  if (absImpactScore >= 0.5)
    return '[&_button]:bg-violet-500 [&_button]:text-white';
  if (absImpactScore >= 0.4)
    return '[&_button]:bg-violet-400 [&_button]:text-white';
  if (absImpactScore >= 0.3)
    return '[&_button]:bg-violet-300 [&_button]:text-black';
  if (absImpactScore >= 0.2)
    return '[&_button]:bg-violet-200 [&_button]:text-black';
  if (absImpactScore > 0)
    return '[&_button]:bg-violet-100 [&_button]:text-black';
  return '[&_button]:bg-gray-100 [&_button]:text-gray-600';
};

export const getOverviewExplanationForHeatmapTable = ({
  explanations,
  dimension,
  treatmentGroup,
  referenceGroup,
  myObservation,
  language,
}: {
  explanations: ExplanationsOutputDictionary | undefined;
  dimension: string;
  treatmentGroup: string;
  referenceGroup: string;
  myObservation: MyObservation;
  language: Phrases;
}) => {
  const treatmentGroupLevelDefinition = getLevelByColumnValueFromObservation(
    myObservation,
    dimension,
    treatmentGroup,
  );
  const referenceGroupLevelDefinition = getLevelByColumnValueFromObservation(
    myObservation,
    dimension,
    referenceGroup,
  );

  const fallbackOverview = language.exploreHeatmapExplanationOverviewFallback({
    treatmentGroupDisplayName:
      treatmentGroupLevelDefinition?.displayName || treatmentGroup,
    referenceGroupDisplayName:
      referenceGroupLevelDefinition?.displayName || referenceGroup,
  });

  const fallbackDescription = '';

  if (!explanations) {
    return {
      overview: fallbackOverview,
      description: fallbackDescription,
    };
  }

  const explanationKey = Object.keys(explanations).find(
    (key) =>
      key ===
      getHeatmapOverviewExplanationOutputKey({
        group: treatmentGroup,
      }),
  );

  if (!explanationKey) {
    return {
      overview: fallbackOverview,
      description: fallbackDescription,
    };
  }

  const explanation = explanations[explanationKey];
  const overview = explanation.overview ?? fallbackOverview;
  const description = explanation.description ?? fallbackDescription;

  return {
    overview,
    description,
  };
};
